<template>
  <Dialog
    is-persistent
    :title="dialogTitle"
    :dialog="isOpened"
    :confirmation-button-text="confirmationButtonText"
    @confirmationAction="addAssignment"
  >
    <template #content>
      <v-form
        ref="form"
        v-model="valid"
      >
        <v-row class="pb-4">
          <v-col>
            <v-autocomplete
              v-model="selectedEmployee"
              :items="availableEmployees"
              :search-input="assignmentItem.searchField"
              class="secondary-background chips-input"
              label="Name"
              item-text="name"
              item-value="id"
              return-object
              outlined
              hide-details
              dense
              :error="isNameError"
              @update:search-input="handleSearchInput"
              @change="employeeSelected($event)"
            />
          </v-col>
        </v-row>
        <v-row class="pb-4">
          <v-col>
            <v-tooltip top>
              <template #activator="{ on }">
                <v-text-field
                  id="assignment-input"
                  v-model="assignmentItem.assignment"
                  class="secondary-background"
                  label="Assignment"
                  type="number"
                  required
                  hide-details="auto"
                  outlined
                  dense
                  :rules="[
                    (v) => {
                      if (v === null || v === undefined || v === '') return true;

                      const num = Number(v);
                      if (num < 0 || num > 2) {
                        return 'Введите число от 0 до 2';
                      }

                      return /^\d+(\.\d{0,5})?$/.test(String(v)) || 'Максимум 5 знаков после запятой'
                    }
                  ]"
                />
              </template>
            </v-tooltip>
          </v-col>
        </v-row>
        <v-row class="pb-4">
          <v-col>
            <v-select
              id="billable-status-input"
              v-model="assignmentItem.billableStatus"
              :menu-props="{ offsetY: true }"
              :items="billableStatusesItems"
              class="secondary-background"
              label="Billable status"
              :rules="[(bs) => !!bs.trim() || 'Billable status is required']"
              hide-details
              required
              outlined
              dense
            />
          </v-col>
        </v-row>
        <v-row class="pb-4">
          <v-col>
            <v-select
              id="availableRoles"
              ref="roleField"
              v-model="assignmentItem.position_ids"
              :menu-props="{ offsetY: true }"
              :items="projectEstimationRoles || []"
              class="secondary-background"
              label="Role(s)"
              item-text="name"
              item-value="id"
              :rules="[(roles) => roles && roles.length > 0 || 'At least one role is required']"
              hide-details
              required
              outlined
              dense
              multiple
              chips
              :error="showRoleError"
            />
          </v-col>
        </v-row>
        <v-row class="pb-4">
          <v-col>
            <v-select
              id="billable-status-input"
              v-model="assignmentItem.status"
              :menu-props="{ offsetY: true }"
              :items="assignmentStatusesItems"
              class="secondary-background"
              label="Assignment status"
              :rules="[(bs) => !!bs.trim() || 'Assignment status is required']"
              hide-details
              required
              outlined
              dense
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col class="pr-2">
            <DatePicker
              ref="startDate"
              v-model="startDate"
              label="Start date"
              :min-date="project.start_date"
              :max-date="project.end_date"
            />
          </v-col>
          <v-col>
            <DatePicker
              ref="endDate"
              v-model="endDate"
              label="End date"
              :min-date="project.start_date"
              :max-date="project.end_date"
            />
          </v-col>
        </v-row>
      </v-form>
    </template>
  </Dialog>
</template>

<script>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';

import { Dialog, DatePicker } from '@/components/shared';
import assignmentStatuses from '@/constants/assignmentStatuses';
import { billableStatuses } from '@/constants/billableStatuses';
import { projectRolesNames } from '@/constants/roles';
import { DateHelper } from '@/helpers/date.helper';

const getInitialStartDate = () => DateHelper.toIso(moment());
const getInitialEndDate = () => DateHelper.toIso(moment().add(1, 'months'));

const getInitialState = () => ({
  searchField: '',
  assignment: 1,
  billableStatus: billableStatuses.billable,
  status: assignmentStatuses.active,
  position_ids: [],
});

export default {
  components: {
    Dialog,
    DatePicker,
  },
  props: {
    isOpened: {
      type: Boolean,
      required: true,
    },
    allEmployees: {
      type: Array,
      required: true,
    },
    involvedEmployees: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      dialogTitle: 'Add Employee',
      confirmationButtonText: 'Add',
      valid: true,
      assignmentItem: getInitialState(),
      selectedEmployee: null,
      employeeId: null,
      startDate: getInitialStartDate(),
      endDate: getInitialEndDate(),
      isNameError: false,
      availableRoles: [],
    };
  },
  computed: {
    ...mapGetters('projects/main', ['project', 'projectEstimationRoles']),
    ...mapGetters('shared/roles', ['projectRoles']),

    form() {
      return { ...this.assignmentItem };
    },
    availableEmployees() {
      const involvedEmployeeIds = this.involvedEmployees.map(
        (e) => e.employee.id
      );

      return this.allEmployees.filter(
        (e) => !involvedEmployeeIds.includes(e.id)
      );
    },
    billableStatusesItems() {
      return Object.values(billableStatuses);
    },
    assignmentStatusesItems() {
      return Object.values(assignmentStatuses);
    },
  },
  watch: {
    startDate(value) {
      if (!DateHelper.isDayBefore(value, this.endDate)) {
        const newEndDate = DateHelper.toIso(moment(value).endOf('month'));

        this.endDate = newEndDate;
      }

      this.startDate = value;
    },
    isOpened: {
      async handler(newVal) {
        if (newVal && this.project?.id) {
          await this.getProjectEstimationRoles({ projectId: this.project.id });
        }
      },
      immediate: true,
    },
  },
  created() {
    document.addEventListener('keydown', this.handleKeypress);
    document.addEventListener('keyup', this.handleKeyup);
  },
  destroyed() {
    document.removeEventListener('keydown', this.handleKeypress);
    document.removeEventListener('keyup', this.handleKeyup);
  },
  methods: {
    ...mapActions('shared/offices', ['getOffices']),
    ...mapActions('projects/main', ['addProjectAssignment', 'getProjectEstimationRoles']),

    employeeSelected(value) {
      if (value) {
        this.employeeId = value.id;
        this.isNameError = false;
      } else {
        this.employeeId = null;
        this.isNameError = true;
      }
    },

    async addAssignment(confirmed) {
      if (!confirmed) {
        this.closeDialog();

        return;
      }

      if (!this.validate()) {
        return;
      }

      if (!this.assignmentItem.position_ids || this.assignmentItem.position_ids.length === 0) {
        this.flashError('No roles available for this project');

        return;
      }

      const assignment = {
        employee_id: this.employeeId,
        assignment: parseFloat(this.assignmentItem.assignment),
        billable_status: this.assignmentItem.billableStatus,
        status: this.assignmentItem.status,
        start_date: this.startDate,
        end_date: this.endDate,
        role_id: this.projectRoles.find(
          (role) => role.title === projectRolesNames.teamMember
        ).id,
        position_ids: this.assignmentItem.position_ids,
      };

      try {
        await this.addProjectAssignment({
          projectId: this.project.id,
          assignment,
        });
        this.$emit('save');
      } finally {
        this.closeDialog();
      }
    },
    closeDialog() {
      this.$emit('close');
      this.reset();
    },
    reset() {
      Object.assign(this.assignmentItem, getInitialState());
      this.selectedEmployee = null;
      this.employeeId = null;
      this.$refs.startDate.date = getInitialStartDate();
      this.$refs.endDate.date = getInitialEndDate();
      this.resetValidation();
    },
    validate() {
      if (!this.employeeId) {
        this.isNameError = true;
      }

      return this.$refs.form.validate() && !this.isNameError;
    },
    handleSearchInput(value) {
      this.assignmentItem.searchField = value;
    },
    handleKeypress(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDialog();
      }
    },
    handleKeyup(event) {
      if (event.key === 'Enter' && event.ctrlKey) {
        event.preventDefault();
        this.addProject();
      }
    },
    resetValidation() {
      this.$refs.form.resetValidation();
      this.isNameError = false;
    },
  },
};
</script>
